import { useCallback, useMemo, useState } from "react";
import moment, { Moment } from "moment";
import {
  IPrioritySchedulerAction,
  PrioritySchedulerHeaderActionType,
} from "@miplatform/components";
import { ATTEMPT_STATUSES, AVAILABILITY_LOG_TYPE } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  IAttemptListItemResponseForWorkOrder,
  IUserListItemResponse,
} from "@miplatform/types";
import { getTimeDifference, isSame } from "@miplatform/utils";
import {
  mdiAccountOutline,
  mdiAccountPlusOutline,
  mdiArrowRightThin,
  mdiCalendarClock,
  mdiCancel,
  mdiClipboardAccountOutline,
} from "@mdi/js";
import { useCalendarContext } from "../../../providers";
import { FOLLOW_UP_TRAY_FILTERS } from "../../Config/Calendars/ConfigCalendarSetList.constants";
import {
  applyCalendarFilters,
  useCalendarFilterGroup,
} from "../CalendarToolBar/useCalendarFilterGroup";
import { buildDateColumns, buildUserColumns } from "../buildColumns.util";
import { useAutoAssignStatusIndicator } from "../useAutoAssignStatusIndicator";
import { useAutoAssignWorkOrders } from "./useAutoAssignWorkOrders";

type ModalUser = IUserListItemResponse | null;

export const useSchedulerViewViewModel = () => {
  const [followUpModalUser, setFollowUpModalUser] = useState<ModalUser>(null);
  const [markInactiveModalUser, setMarkInactiveModalUser] =
    useState<ModalUser>(null);
  const [removeInactiveModalUser, setRemoveInactiveModalUser] =
    useState<ModalUser>(null);
  const [clearScheduleModalUser, setClearScheduleModalUser] =
    useState<ModalUser>(null);
  const { navigate } = useRouter();

  const {
    showFilters,
    toggleFilters,
    filters,
    appliedFilters,
    statusOptions,
    hasPendingFilters,
    handleApplyFilters,
    handleResetFilters,
    handleFilterChange,
  } = useCalendarFilterGroup();

  const {
    assignableUsers,
    calendarSet,
    highlightedAttempts,
    date,
    events,
    isCalendarLoading,
    setZoom,
    zoom,
    users,
    isAssigneeMode,
    tenantHolidays,
    handleSelectUser,
    selectedUser,
    trayBuckets,
    loadUsers,
    clearWorkOrderSchedule,
    unhighlightAttempts,
    setSelectedAttempt,
    getTrayDataForCalendar,
    activeCalendarId,
    refreshData,
    verticalTabActiveFilter,
    assigneeModeUser,
    isScheduler,
    autoAssignStatusUpdates,
  } = useCalendarContext();

  const [selectedDate, setSelectedDate] = useState<Moment>(date);

  const handleAutoAssignSuccess = useCallback(() => {
    refreshData();
    getTrayDataForCalendar();
  }, [refreshData, getTrayDataForCalendar]);

  const { autoAssignWorkOrders, isAutoAssignLoading } = useAutoAssignWorkOrders(
    calendarSet?.config?.id,
    handleAutoAssignSuccess
  );
  const { isAutoAssignActive, loadAutoAssignStatuses } =
    useAutoAssignStatusIndicator({
      calendarSetId: calendarSet?.config?.id,
      eventType: calendarSet?.config?.eventType,
      autoAssignStatusUpdates,
    });

  const handleFollowUpAutoAssign = useCallback(async () => {
    await autoAssignWorkOrders();
    await loadAutoAssignStatuses();
  }, [autoAssignWorkOrders, loadAutoAssignStatuses]);

  const headerActions = useMemo((): IPrioritySchedulerAction[] => {
    const hasInactiveAvailabilityLog = (user: IUserListItemResponse) =>
      !!user.availabilityLog?.some(
        (log) => log.type === AVAILABILITY_LOG_TYPE.INACTIVE
      );

    return [
      {
        label: "View",
        icon: mdiArrowRightThin,
        type: PrioritySchedulerHeaderActionType.View,
        actions: [
          {
            label: "View More",
            icon: mdiArrowRightThin,
            type: PrioritySchedulerHeaderActionType.ViewMore,
          },
          {
            label: "View Profile",
            icon: mdiAccountOutline,
            type: PrioritySchedulerHeaderActionType.ViewProfile,
          },
        ],
      },
      {
        label: "Scheduling",
        icon: mdiCalendarClock,
        type: PrioritySchedulerHeaderActionType.Scheduling,
        actions: [
          {
            label: "Mark Inactive",
            icon: mdiClipboardAccountOutline,
            type: PrioritySchedulerHeaderActionType.MarkInactive,
          },
          {
            label: "Remove Inactive",
            icon: mdiClipboardAccountOutline,
            type: PrioritySchedulerHeaderActionType.RemoveInactive,
            getIsActionHidden: (user) => !hasInactiveAvailabilityLog(user),
          },
          {
            label: "Clear Schedule",
            icon: mdiCancel,
            iconColor: (theme) => theme.palette.error.main,
            type: PrioritySchedulerHeaderActionType.ClearSchedule,
            getIsActionHidden: (user) => !isScheduler,
          },
        ],
      },
      {
        label: "Add from Follow-Up",
        icon: mdiAccountPlusOutline,
        type: PrioritySchedulerHeaderActionType.AddFromFollowUp,
      },
    ];
  }, [isScheduler]);

  const activeTrays = useMemo(() => {
    if (!trayBuckets?.length) return [];

    const bucketsById = trayBuckets.reduce((acc, bucket) => {
      acc[bucket.id] = bucket;
      return acc;
    }, {} as Record<string, (typeof trayBuckets)[0]>);

    return trayBuckets.reduce((acc, tray) => {
      if (tray.isActive) {
        const bucket = bucketsById[tray.id];
        if (bucket) {
          acc.push(bucket);
        }
      }
      return acc;
    }, [] as (typeof trayBuckets)[0][]);
  }, [trayBuckets]);

  const selectedAssignee = assigneeModeUser || selectedUser;

  const filteredEvents = useMemo(
    () =>
      applyCalendarFilters(
        events,
        appliedFilters,
        verticalTabActiveFilter
      ).filter(({ attemptStatus }) => attemptStatus !== ATTEMPT_STATUSES.HOLD),
    [events, appliedFilters, verticalTabActiveFilter]
  );

  const followUpAttempts = useMemo(
    (): IAttemptListItemResponseForWorkOrder[] =>
      trayBuckets
        .filter(({ filter }) => filter === FOLLOW_UP_TRAY_FILTERS)
        .flatMap(
          ({ data }) => (data ?? []) as IAttemptListItemResponseForWorkOrder[]
        ),
    [trayBuckets]
  );

  const columns = useMemo(() => {
    const currentUsers = users.length > 0 ? users : assignableUsers;

    if (!currentUsers) return [];

    if (selectedAssignee) {
      return buildDateColumns({
        date,
        events: filteredEvents,
        user: currentUsers.find((user) => user.id === selectedAssignee.id),
      });
    }
    return buildUserColumns({
      users: currentUsers,
      date,
      events: filteredEvents,
    });
  }, [assignableUsers, users, filteredEvents, date, selectedAssignee]);

  const assignedAttemptsForFollowUpUser = useMemo(() => {
    if (!followUpModalUser) return [];
    return columns
      .flatMap((column) => column.events || [])
      .filter((event) => event.assignedUserId === followUpModalUser.id);
  }, [columns, followUpModalUser]);

  const handleHeaderActionClick = useCallback(
    (
      actionType: PrioritySchedulerHeaderActionType,
      user?: IUserListItemResponse,
      date?: Moment
    ) => {
      if (!user) return;

      if (date) {
        setSelectedDate(date);
      }

      const resolvedUser = users.find((u) => u.id === user.id) || user;

      switch (actionType) {
        case PrioritySchedulerHeaderActionType.ViewMore:
          handleSelectUser(resolvedUser);
          break;
        case PrioritySchedulerHeaderActionType.AddFromFollowUp:
          setFollowUpModalUser(resolvedUser);
          break;
        case PrioritySchedulerHeaderActionType.MarkInactive:
          handleMarkInactive(resolvedUser, date ?? moment());
          break;
        case PrioritySchedulerHeaderActionType.RemoveInactive:
          setRemoveInactiveModalUser(resolvedUser);
          break;
        case PrioritySchedulerHeaderActionType.ClearSchedule:
          setClearScheduleModalUser(resolvedUser);
          break;
        case PrioritySchedulerHeaderActionType.ViewProfile:
          navigate(`/profile/${resolvedUser?.id}`);
          break;
        default:
          break;
      }
    },
    [users, navigate, handleSelectUser]
  );

  const handleClearScheduleConfirm = useCallback(() => {
    if (!clearScheduleModalUser) return;
    clearWorkOrderSchedule(clearScheduleModalUser);
    setClearScheduleModalUser(null);
  }, [clearScheduleModalUser, clearWorkOrderSchedule]);

  const handleBack = useCallback(() => {
    handleSelectUser(null);
  }, [handleSelectUser]);

  const closeFollowUpModal = useCallback(() => {
    setFollowUpModalUser(null);
  }, []);

  const closeMarkInactiveModal = useCallback(() => {
    setMarkInactiveModalUser(null);
  }, []);

  const closeRemoveInactiveModal = useCallback(() => {
    setRemoveInactiveModalUser(null);
  }, []);

  const closeClearScheduleModal = useCallback(() => {
    setClearScheduleModalUser(null);
  }, []);

  const handleMarkInactive = useCallback(
    (user: IUserListItemResponse, date: Moment) => {
      const hasAlreadyBeenMarkedInactive = user.availabilityLog
        ?.filter((log) => log.type === AVAILABILITY_LOG_TYPE.INACTIVE)
        .some((log) => {
          return (
            isSame({ date1: date, date2: log?.from, timeUnit: "day" }) &&
            getTimeDifference({
              start: log?.from,
              end: log?.to,
              timeUnit: "hours",
              precise: true,
            }) >= 23
          );
        });

      if (hasAlreadyBeenMarkedInactive) {
        toastService.info({
          text: "User has already been marked inactive for this date",
        });
      } else {
        setMarkInactiveModalUser(user);
      }
    },
    []
  );

  const toolbarTitle = selectedAssignee
    ? `${selectedAssignee.displayName}'s Schedule`
    : "Schedule";

  const showToolbarBackBtn = !isAssigneeMode && !!selectedAssignee;

  return {
    zoom,
    setZoom,
    showFilters,
    toggleFilters,
    filters,
    appliedFilters,
    statusOptions,
    hasPendingFilters,
    handleApplyFilters,
    handleResetFilters,
    handleFilterChange,
    workTemplateBases: calendarSet?.config?.workTemplateBases || [],
    showToolbarBackBtn,
    handleBack,
    toolbarTitle,

    columns,
    date,
    highlightedAttempts,
    tenantHolidays,
    calendarSetConfig: calendarSet?.config ?? null,
    headerActions,
    unhighlightAttempts,
    handleHeaderActionClick,
    setSelectedAttempt,
    activeTrays,
    isAutoAssignLoading,
    isAutoAssignActive,
    isAssigneeMode,
    handleFollowUpAutoAssign,

    getTrayDataForCalendar,
    handleSelectUser,

    clearScheduleModalUser,
    closeClearScheduleModal,
    handleClearScheduleConfirm,
    isCalendarLoading,
    followUpModalUser,
    closeFollowUpModal,
    assignedAttemptsForFollowUpUser,
    followUpAttempts,
    removeInactiveModalUser,
    closeRemoveInactiveModal,
    markInactiveModalUser,
    closeMarkInactiveModal,
    loadUsers,
    selectedDate,
    activeCalendarId,
  };
};
